import React from 'react';

const DesignEngineeringPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-primary-500 text-secondary-500 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-3xl md:text-5xl font-bold mb-4">
              Design Engineering
            </h1>
            <p className="text-lg md:text-2xl text-secondary-100 max-w-3xl mx-auto">
              Innovative engineering solutions for industrial applications
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Image Section - Left side on desktop, top on mobile */}
          <div className="lg:sticky lg:top-36 order-1 lg:order-1 animate-fade-in-left">
            <div className="rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
              <img
                src="/de.png"
                alt="Design Engineering"
                className="w-full h-full object-contain"
              />
            </div>
          </div>

          {/* Content Section - Right side on desktop, bottom on mobile */}
          <div className="space-y-6 md:space-y-8 order-2 lg:order-2 animate-fade-in-right">
            {/* Our Expertise */}
            <div
              className="animate-slide-up"
              style={{ animationDelay: '0.1s' }}
            >
              <h2 className="text-2xl md:text-3xl font-bold text-primary-500 mb-4 md:mb-6">
                Our Engineering & Design Expertise
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  'Storage Tank',
                  'Industrial Shed',
                  'Seal Pot',
                  'Coke Drum Chute',
                  'Lifting Equipment',
                  'Industrial Structure & Equipment Supports',
                  'Calculation',
                  'SS Strainer',
                  'Cover Casting',
                ].map((item, index) => (
                  <div
                    key={index}
                    className="bg-white p-4 rounded-lg shadow-md border-l-4 border-primary-500 animate-fade-in hover:shadow-lg transition-shadow duration-200"
                    style={{ animationDelay: `${0.2 + index * 0.05}s` }}
                  >
                    <span className="font-medium text-gray-800">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Our Process */}
            <div
              className="animate-slide-up"
              style={{ animationDelay: '0.3s' }}
            >
              <h3 className="text-xl md:text-2xl font-bold text-primary-500 mb-4 md:mb-6">
                Our Design Process
              </h3>
              <div className="space-y-4">
                {[
                  'Studying a design brief',
                  'Thinking of possible design solutions',
                  'Researching whether the design will work and be cost-effective',
                  'Assessing the usability, environmental impact and safety of a design',
                  'Using computer-aided design (CAD) and computer-assisted engineering (CAE) software to create prototypes',
                  'Collecting and analyzing data from tests on prototypes',
                  'Modifying designs and retesting them',
                  'Writing regular progress reports and presenting them to project managers and clients',
                ].map((step, index) => (
                  <div
                    key={index}
                    className="flex items-start space-x-4 animate-fade-in hover:translate-x-2 transition-transform duration-200"
                    style={{ animationDelay: `${0.4 + index * 0.1}s` }}
                  >
                    <div className="flex-shrink-0 w-8 h-8 bg-primary-500 text-secondary-500 rounded-full flex items-center justify-center font-bold text-sm">
                      {index + 1}
                    </div>
                    <p className="text-sm md:text-base text-gray-700 leading-relaxed pt-1">
                      {step}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Technologies */}
            <div
              className="animate-slide-up"
              style={{ animationDelay: '0.5s' }}
            >
              <h3 className="text-xl md:text-2xl font-bold text-primary-500 mb-4">
                Technologies We Use
              </h3>
              <div className="flex flex-wrap gap-3">
                {[
                  'CAD Software',
                  'CAE Software',
                  'Prototyping',
                  'Testing & Analysis',
                ].map((tech, index) => (
                  <span
                    key={index}
                    className="bg-primary-100 text-primary-500 px-4 py-2 rounded-full text-sm font-medium animate-fade-in hover:bg-primary-200 transition-colors duration-200"
                    style={{ animationDelay: `${0.6 + index * 0.1}s` }}
                  >
                    {tech}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Call to Action Section */}
      <div className="bg-primary-500 text-secondary-500 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-4">
            Ready to Start Your Design Project?
          </h2>
          <p className="text-lg md:text-xl text-secondary-100 mb-8 max-w-2xl mx-auto">
            Let our experienced design engineers bring your industrial projects
            to life with precision and innovation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/contact"
              className="bg-secondary-500 text-primary-500 px-8 py-3 rounded-lg font-semibold hover:bg-secondary-100 transition-colors cursor-pointer"
            >
              Get Quote
            </a>
            <a
              href="/services"
              className="border-2 border-secondary-500 text-secondary-500 px-8 py-3 rounded-lg font-semibold hover:bg-secondary-500 hover:text-primary-500 transition-colors cursor-pointer"
            >
              View All Services
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DesignEngineeringPage;
